// ===========================================
// PRODUCTS (CPG Hub — catalog × inventory, one sheet)
// ===========================================
// The answer to "where's products?": every catalog item with its SKU, the
// wholesale price it sells at, the unit cost it carries in Inventory, the
// margin between them, and what's on the shelf. Joined by SKU (the code lives
// in the catalog description as "SKU 0001"), with an exact-name fallback.
// Price edits write to the catalog; cost edits write to Inventory — the same
// records every other page reads.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuApi, inventoryApi } from '../services/api';
import { Package, X, Leaf, Rocket, Pencil } from 'lucide-react';
import clsx from 'clsx';
import { fmtMoney, parseSku, sizeFrom } from '../utils/cpg';
import ShopifyCatalog, { useShopifyCatalog } from '../components/ShopifyCatalog';

const toCents = (dollars: string) => Math.round((parseFloat(dollars) || 0) * 100);

type ProductRow = {
  menuItem: any;
  sku: string | null;
  inventoryItem: any | null;
  priceCents: number;
  costCents: number | null; // from inventory, null when no match / no cost
  marginPct: number | null;
  organic: boolean;
  launching: boolean;
};

export default function ProductsPage() {
  const qc = useQueryClient();
  const shopifyCatalog = useShopifyCatalog();
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const say = (tone: 'success' | 'error', text: string) => setBanner({ tone, text });
  const oops = (e: any, fallback: string) => say('error', e?.response?.data?.error?.message || fallback);

  // Same keys + shapes the rest of the dashboard uses, so caches stay shared.
  const { data: menuData, isPending: menuPending } = useQuery({
    queryKey: ['menu-items-flat'],
    queryFn: async () => {
      const res = await menuApi.getItems();
      return res.data?.data || res.data || [];
    },
    staleTime: 60_000,
  });
  const menuItems: any[] = (Array.isArray(menuData) ? menuData : (menuData as any)?.items || []).filter((m: any) => m?.id);
  const { data: inventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  const { rows, unmatchedInventory } = useMemo(() => {
    const bySku = new Map<string, any>();
    const byName = new Map<string, any>();
    for (const i of inventory as any[]) {
      if (i.sku) bySku.set(String(i.sku).trim().toLowerCase(), i);
      byName.set(String(i.name || '').trim().toLowerCase(), i);
    }
    const used = new Set<string>();
    const rows: ProductRow[] = menuItems.map((m) => {
      const sku = parseSku(m.description);
      const inv = (sku ? bySku.get(sku.toLowerCase()) : undefined) || byName.get(String(m.name || '').trim().toLowerCase()) || null;
      if (inv) used.add(inv.id);
      const priceCents = m.priceCents || 0;
      const costCents = inv && parseFloat(String(inv.unitCost)) > 0 ? Math.round(parseFloat(String(inv.unitCost)) * 100) : null;
      const tags: string[] = Array.isArray(m.tags) ? m.tags : [];
      return {
        menuItem: m, sku, inventoryItem: inv, priceCents, costCents,
        marginPct: priceCents > 0 && costCents != null ? Math.round(((priceCents - costCents) / priceCents) * 100) : null,
        organic: tags.some((t) => String(t).toLowerCase() === 'organic'),
        launching: /launching/i.test(m.description || ''),
      };
    });
    const unmatchedInventory = (inventory as any[]).filter((i) => !used.has(i.id));
    return { rows, unmatchedInventory };
  }, [menuItems, inventory]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((r) => r.menuItem.id === selectedId) || null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['menu-items-flat'] });
    qc.invalidateQueries({ queryKey: ['inventory'] });
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><Package className="w-6 h-6 text-primary-600" /> Products</h1>
        <p className="text-sm md:text-base text-slate-500">Your connected Shopify listings and wholesale catalog, with warehouse stock and costs where available.</p>
      </div>

      <ShopifyCatalog query={shopifyCatalog} />

      {banner && (
        <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
          banner.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
          <p>{banner.text}</p>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {menuPending ? (
        <div className="card p-8 text-center text-slate-400">Loading products…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-slate-500">
          {shopifyCatalog.data?.connected ? 'No wholesale catalog has been imported yet. Shopify listings are shown above.' : 'No wholesale products yet. Add your catalog with names, SKUs, and wholesale prices; warehouse stock and unit costs come from Inventory.'}
        </div>
      ) : (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                  <th className="px-3 py-2 font-medium text-left">Product</th>
                  <th className="px-3 py-2 font-medium text-left">SKU</th>
                  <th className="px-3 py-2 font-medium text-right" title="From the catalog — what a wholesale case sells for">Wholesale price</th>
                  <th className="px-3 py-2 font-medium text-right" title="From Inventory — what one unit costs you">Unit cost</th>
                  <th className="px-3 py-2 font-medium text-right">Margin</th>
                  <th className="px-3 py-2 font-medium text-right">On hand</th>
                  <th className="px-3 py-2 font-medium text-right" title="Weeks of cover at the trailing 4-week wholesale pace">Cover</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.menuItem.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setSelectedId(r.menuItem.id)}>
                    <td className="px-3 py-2.5 max-w-[280px]">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-slate-900 truncate">{r.menuItem.name}</p>
                        {r.organic && <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" title="Tagged organic — tracked on the Imports organic balance"><Leaf className="w-2.5 h-2.5" />organic</span>}
                        {r.launching && <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title="Marked launching in the catalog"><Rocket className="w-2.5 h-2.5" />launching</span>}
                      </div>
                      {!r.inventoryItem && <p className="text-[11px] text-slate-400">not in Inventory — no stock or cost to show</p>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{r.sku || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-slate-900">{fmtMoney(r.priceCents)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-600">{r.costCents != null ? fmtMoney(r.costCents) : <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {r.marginPct != null
                        ? <span className={clsx('font-medium', r.marginPct < 20 ? 'text-red-600' : r.marginPct < 40 ? 'text-amber-600' : 'text-emerald-700')}>{r.marginPct}%</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-600">
                      {r.inventoryItem ? `${parseFloat(String(r.inventoryItem.currentQuantity)) || 0} ${r.inventoryItem.unit || ''}`.trim() : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {r.inventoryItem?.weeksOfCover != null
                        ? <span className={clsx(parseFloat(String(r.inventoryItem.weeksOfCover)) < 2 ? 'text-red-600 font-medium' : 'text-slate-600')}>{parseFloat(String(r.inventoryItem.weeksOfCover))}w</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400">
            Margin = wholesale price vs Inventory unit cost. Click a row to see both records and edit price or cost.
          </p>
          {unmatchedInventory.length > 0 && (
            <p className="text-xs text-slate-400">
              {unmatchedInventory.length} Inventory item{unmatchedInventory.length === 1 ? ' isn’t' : 's aren’t'} in the catalog:{' '}
              {unmatchedInventory.slice(0, 5).map((i: any) => i.name).join(', ')}{unmatchedInventory.length > 5 ? '…' : ''} — give the catalog item the same SKU (or exact name) and they link up.
            </p>
          )}
        </>
      )}

      {selected && (
        <ProductPanel row={selected} onClose={() => setSelectedId(null)} refresh={refresh} say={say} oops={oops} />
      )}
    </div>
  );
}

// ===========================================
// PRODUCT PANEL — both records, inline price/cost edit
// ===========================================
function ProductPanel({ row, onClose, refresh, say, oops }: {
  row: ProductRow; onClose: () => void; refresh: () => void;
  say: (tone: 'success' | 'error', text: string) => void; oops: (e: any, fallback: string) => void;
}) {
  const m = row.menuItem;
  const inv = row.inventoryItem;
  const [editPrice, setEditPrice] = useState(false);
  const [priceStr, setPriceStr] = useState('');
  const [editCost, setEditCost] = useState(false);
  const [costStr, setCostStr] = useState('');

  const savePrice = useMutation({
    mutationFn: () => menuApi.updateItem(m.id, { priceCents: toCents(priceStr) }),
    onSuccess: () => { refresh(); setEditPrice(false); say('success', 'Wholesale price updated — new orders pick it up; existing orders keep theirs.'); },
    onError: (e: any) => oops(e, "Couldn't update the price."),
  });
  const saveCost = useMutation({
    mutationFn: () => inventoryApi.updateItem(inv.id, { unitCost: parseFloat(costStr) || 0 }),
    onSuccess: () => { refresh(); setEditCost(false); say('success', 'Unit cost updated in Inventory — margins recompute from it.'); },
    onError: (e: any) => oops(e, "Couldn't update the cost."),
  });

  // Price per liter / per 100 g where the name carries a size — same parse the
  // Imports landed-cost sheet uses ("500ml", "8 oz").
  const size = sizeFrom(m.name, row.sku, inv?.name);
  const perSize = (cents: number | null) => {
    if (cents == null) return null;
    if (size?.liters) return `${fmtMoney(Math.round(cents / size.liters))}/L`;
    if (size?.grams) return `${fmtMoney(Math.round(cents / (size.grams / 100)))}/100g`;
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-5 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900">{m.name}</h2>
              {row.organic && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded"><Leaf className="w-2.5 h-2.5" />organic</span>}
              {row.launching && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded"><Rocket className="w-2.5 h-2.5" />launching</span>}
            </div>
            <p className="text-sm text-slate-500">{row.sku ? `SKU ${row.sku}` : 'No SKU in the catalog description'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg shrink-0"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {/* Catalog record */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Catalog (sell side)</p>
          <div className="rounded-lg bg-slate-50 p-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-500">Wholesale price</span>
              {!editPrice ? (
                <span className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-900">{fmtMoney(row.priceCents)}</span>
                  <button onClick={() => { setPriceStr((row.priceCents / 100).toFixed(2)); setEditPrice(true); }} className="p-1 text-slate-400 hover:text-primary-600" title="Edit price"><Pencil className="w-3.5 h-3.5" /></button>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="text-slate-400">$</span>
                  <input type="number" min="0" step="0.01" value={priceStr} onChange={(e) => setPriceStr(e.target.value)} className="input w-24 py-1 text-sm" autoFocus />
                  <button onClick={() => savePrice.mutate()} disabled={savePrice.isPending || !(parseFloat(priceStr) >= 0)} className="btn btn-sm btn-primary">{savePrice.isPending ? '…' : 'Save'}</button>
                  <button onClick={() => setEditPrice(false)} className="btn btn-sm btn-ghost"><X className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
            {perSize(row.priceCents) && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">Price per {size?.liters ? 'liter' : '100 g'}</span>
                <span className="text-slate-700">{perSize(row.priceCents)}</span>
              </div>
            )}
            {m.description && <p className="text-xs text-slate-500 whitespace-pre-wrap pt-1 border-t border-slate-200/60">{m.description}</p>}
            {Array.isArray(m.tags) && m.tags.length > 0 && (
              <p className="text-xs text-slate-400">Tags: {m.tags.join(', ')}</p>
            )}
          </div>
        </div>

        {/* Inventory record */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Inventory (stock &amp; cost)</p>
          {!inv ? (
            <p className="text-sm text-slate-400">
              Not matched to an Inventory item — add one with SKU {row.sku || '(set a SKU in the catalog description first)'} or the exact same name, and stock, cost and cover appear here.
            </p>
          ) : (
            <div className="rounded-lg bg-slate-50 p-3 space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">Unit cost</span>
                {!editCost ? (
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-900">{row.costCents != null ? fmtMoney(row.costCents) : 'not set'}</span>
                    <button onClick={() => { setCostStr(row.costCents != null ? (row.costCents / 100).toFixed(2) : ''); setEditCost(true); }} className="p-1 text-slate-400 hover:text-primary-600" title="Edit cost"><Pencil className="w-3.5 h-3.5" /></button>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <span className="text-slate-400">$</span>
                    <input type="number" min="0" step="0.01" value={costStr} onChange={(e) => setCostStr(e.target.value)} className="input w-24 py-1 text-sm" autoFocus />
                    <button onClick={() => saveCost.mutate()} disabled={saveCost.isPending || !(parseFloat(costStr) >= 0)} className="btn btn-sm btn-primary">{saveCost.isPending ? '…' : 'Save'}</button>
                    <button onClick={() => setEditCost(false)} className="btn btn-sm btn-ghost"><X className="w-3.5 h-3.5" /></button>
                  </span>
                )}
              </div>
              {perSize(row.costCents) && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500">Cost per {size?.liters ? 'liter' : '100 g'}</span>
                  <span className="text-slate-700">{perSize(row.costCents)}</span>
                </div>
              )}
              {row.marginPct != null && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500">Margin</span>
                  <span className={clsx('font-semibold', row.marginPct < 20 ? 'text-red-600' : row.marginPct < 40 ? 'text-amber-700' : 'text-emerald-700')}>{row.marginPct}%</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">On hand</span>
                <span className="text-slate-900 font-medium">{parseFloat(String(inv.currentQuantity)) || 0} {inv.unit || ''}</span>
              </div>
              {inv.onOrderQuantity > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500">On order</span>
                  <span className="text-slate-700">{inv.onOrderQuantity} {inv.unit || ''}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">Weeks of cover</span>
                <span className="text-slate-700">{inv.weeksOfCover != null ? `${parseFloat(String(inv.weeksOfCover))} weeks at the recent pace` : 'no recent wholesale pace'}</span>
              </div>
              {inv.sku && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500">Inventory SKU</span>
                  <span className="text-slate-700">{inv.sku}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-400">
          Price edits write to the catalog and apply to new orders; cost edits write to Inventory and land in cost history, so margins everywhere recompute.
        </p>
      </div>
    </div>
  );
}
